/* eslint-disable no-nested-ternary */
import { InboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { Button } from '../../../tailwindComponents/Button/Button';

interface GoodsReceivedNoteDetailsProps {
  selectedInboundProcess: InboundProcess;
  onClose: () => void;
  embedded?: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <polyline points='12 6 12 12 16 14' />
    </svg>
  );
}

export function GoodsReceivedNoteDetails({
  selectedInboundProcess,
  onClose,
  embedded,
  isDesktop,
  isTablet,
}: GoodsReceivedNoteDetailsProps) {
  const { userBusinessName, status, notices } = selectedInboundProcess;
  const { estimatedTimeOfArrival, shipmentNoteNumber } =
    selectedInboundProcess.shipmentNote;
  const createdAt = selectedInboundProcess.goodsReceivedNote?.createdAt;
  const skus = selectedInboundProcess.goodsReceivedNote?.skus;
  const comment = selectedInboundProcess.goodsReceivedNote?.comment;
  const goodsReceivedNoteNumber =
    selectedInboundProcess.goodsReceivedNote?.grnNoteNumber;

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete =
    !!selectedInboundProcess.goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(
      goodsReceivedNoteNumber || shipmentNoteNumber
    );
  };

  return (
    <div className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Goods received note #{goodsReceivedNoteNumber || shipmentNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy goods received note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Client name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Client name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {userBusinessName}
          </span>
        </div>

        {/* Creation date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Creation date</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(createdAt).format('D MMM YYYY')}
          </span>
        </div>

        {/* Status */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <StatusIcon />
            <span className='text-xs font-medium'>Status</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>{status}</span>
        </div>

        {/* Created by */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Created by</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {selectedInboundProcess.goodsReceivedNote?.finalisedByName}
          </span>
        </div>
      </div>

      {/* Additional Info Row */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Original ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Original ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs
              .utc(estimatedTimeOfArrival.dateTime)
              .tz(estimatedTimeOfArrival.timeZone)
              .format('D MMM YYYY')}
          </span>
        </div>

        {/* Actual date of arrival */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Actual arrival</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(createdAt).format('D MMM YYYY')}
          </span>
        </div>
      </div>

      {/* Issues */}
      {hasIssues && (
        <div className='px-6 py-4 border-b border-gray-200'>
          <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
            Issues
          </p>
          <div className='space-y-2'>
            {notices?.map((notice) => (
              <div
                key={notice}
                className={`flex items-center gap-2 p-2 rounded ${
                  hasIssuesBeforeProcessIsComplete ? 'bg-amber-50' : 'bg-red-50'
                }`}
              >
                <ExclamationTriangleIcon
                  className={`w-4 h-4 flex-shrink-0 ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                />
                <span
                  className={`text-sm ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-800'
                      : 'text-red-800'
                  }`}
                >
                  {notice}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>Items</h3>

        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div
            className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'
            data-testid='sku-grid'
          >
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Despatched
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Delivered
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Discrepancy
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-4' data-testid='sku-items-container'>
          {skus?.map((sku, index) => {
            const isDiscrepancy = sku.discrepancyReason !== null;
            const { despatchedSkuQuantity, receivedSkuQuantity } = sku;
            const discrepancyAmount =
              despatchedSkuQuantity - receivedSkuQuantity;
            const isNegativeDiscrepancy = discrepancyAmount > 0;

            return (
              <div
                key={sku.skuId}
                className={`${
                  index !== skus.length - 1
                    ? 'pb-4 border-b border-gray-100'
                    : ''
                }`}
              >
                {isDesktop ? (
                  /* Desktop Layout */
                  <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 items-start'>
                    {/* Index */}
                    <div className='flex justify-center'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* SKU ID */}
                    <span className='text-sm text-gray-900'>
                      <SkuInfoLink inventorySkuId={sku.skuId}>
                        {sku.skuIdentifier}
                      </SkuInfoLink>
                    </span>
                    {/* SKU name */}
                    <span className='text-sm text-gray-900'>{sku.name}</span>
                    {/* Despatched quantity */}
                    <span className='text-sm text-gray-900'>
                      {despatchedSkuQuantity}
                    </span>
                    {/* Delivered quantity */}
                    <div className='text-sm text-gray-900'>
                      {receivedSkuQuantity}
                      {sku.discrepancyReason && (
                        <span className='text-red-600 ml-1'>
                          ({isNegativeDiscrepancy ? '-' : '+'}
                          {isNegativeDiscrepancy
                            ? discrepancyAmount
                            : discrepancyAmount.toString().substring(1)}
                          )
                        </span>
                      )}
                    </div>
                    {/* Discrepancy reason */}
                    <div className='text-sm text-gray-600'>
                      {isDiscrepancy && sku.discrepancyReason}
                    </div>
                  </div>
                ) : (
                  /* Mobile/Tablet Layout */
                  <div className='flex gap-3'>
                    {/* Index */}
                    <div className='flex-shrink-0'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* Details */}
                    <div
                      className={`flex-1 grid gap-3 ${
                        isTablet ? 'grid-cols-2' : 'grid-cols-1'
                      }`}
                    >
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          SKU ID
                        </span>
                        <span className='text-sm text-gray-900'>
                          <SkuInfoLink inventorySkuId={sku.skuId}>
                            {sku.skuIdentifier}
                          </SkuInfoLink>
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          SKU name
                        </span>
                        <span className='text-sm text-gray-900'>
                          {sku.name}
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          Despatched Qty
                        </span>
                        <span className='text-sm text-gray-900'>
                          {despatchedSkuQuantity}
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          Delivered Qty
                        </span>
                        <span className='text-sm text-gray-900'>
                          {receivedSkuQuantity}
                          {sku.discrepancyReason && (
                            <span className='text-red-600 ml-1'>
                              ({isNegativeDiscrepancy ? '-' : '+'}
                              {isNegativeDiscrepancy
                                ? discrepancyAmount
                                : discrepancyAmount.toString().substring(1)}
                              )
                            </span>
                          )}
                        </span>
                      </div>
                      {isDiscrepancy && (
                        <div
                          className={`flex flex-col gap-0.5 ${
                            isTablet ? 'col-span-2' : ''
                          }`}
                        >
                          <span className='text-xs font-medium text-gray-500'>
                            Discrepancy reason
                          </span>
                          <span className='text-sm text-gray-600'>
                            {sku.discrepancyReason}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comments Section */}
      {comment && (
        <div
          className='px-6 py-4 border-t border-gray-200'
          data-testid='grn-comment'
        >
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Comment</h3>
          <p className='text-sm text-gray-600'>{comment}</p>
        </div>
      )}

      {!embedded && (
        <div
          className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
            isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
          }`}
        >
          <Button
            label='Close'
            intent='secondary'
            size='sm'
            onClick={onClose}
            className={!isDesktop ? 'w-full' : ''}
          />
        </div>
      )}
    </div>
  );
}
